import { LobbyImage } from '@colonist/types';
import { OnlineStatus } from '../../../../shared/friends/FriendTypes.ts';
import { SocketLobbySend } from '../socket/SocketLobbySend.ts';
import { UIUtilsNotifications } from '../notifications/UIUtilsNotifications.ts';
import { TimeUtils } from '@colonist/utils';
import { NotificationTypeEnum } from '../../../../shared/NotificationTypes.ts';
import { socket, uiManagerEvents } from '../EntryShared.ts';
import { AudioManager, AudioType } from '@colonist/audio';
export class UIOnlineFriendController {
    init() {
        socket.controller.registerSessionEstablishedObserver(()=>{
            this.removeAllInvites();
            UIOnlineFriendController.registerToFriendService();
        });
    }
    static registerToFriendService() {
        SocketLobbySend.registerToFriendService();
        SocketLobbySend.changedOnlineStatus(this.lastOnlineStatus);
        UIUtilsNotifications.delayNotificationDataRequest(()=>SocketLobbySend.getAllFriendRoomInvitesReceived());
    }
    static setOnlineStatus(onlineStatus) {
        this.lastOnlineStatus = onlineStatus;
        SocketLobbySend.changedOnlineStatus(onlineStatus);
    }
    static userOnlineAndIdle() {
        this.setOnlineStatus(OnlineStatus.OnlineAndIdle);
    }
    static userEnteredRoom() {
        this.setOnlineStatus(OnlineStatus.InRoom);
        UIUtilsNotifications.delayNotificationDataRequest(()=>SocketLobbySend.getAllFriendsOnlineStatus());
    }
    static userEnteredGame() {
        this.setOnlineStatus(OnlineStatus.InGame);
    }
    static inviteFriendToRoom(receiverUserId, roomId) {
        SocketLobbySend.inviteFriendToRoom(receiverUserId, roomId);
    }
    static respondToFriendRoomInvite(roomId, accept, senderUserId) {
        if (accept) uiManagerEvents.leaveRoomAndJoinRoom(roomId);
        SocketLobbySend.respondToFriendRoomInvite(roomId, accept, senderUserId);
    }
    updateFriendOnlineStatus(friendData) {
        this.displayNotificationIfUserCameOnline(friendData);
    }
    updateAllFriendsOnlineStatus(friends) {
        this.onlineFriendUsers.clear();
        for (const friend of friends)this.onlineFriendUsers.set(friend.userId, friend);
    }
    friendRespondedToRoomInvite(responseData) {
        if (responseData.response) return;
        const declinedTranslationObj = {
            key: 'strings:roomPage.declinedInvite',
            options: {
                username: responseData.responderUsername
            }
        };
        this.showNotification(LobbyImage.Lightning, declinedTranslationObj);
    }
    receivedRoomInvites(roomInvites) {
        if (roomInvites.length > 0) AudioManager.play(AudioType.RoomSettingUpdated);
        for (const roomInvite of roomInvites){
            this.showRoomInvitePopupNotification(roomInvite);
            this.addRoomInviteNotificationToPanel(roomInvite);
        }
    }
    receivedFriendRequest(friendRequest) {
        AudioManager.play(AudioType.RoomSettingUpdated);
        this.showFriendRequestPopupNotification(friendRequest);
        this.addFriendRequestNotificationToPanel(friendRequest);
    }
    addRoomInviteNotificationToPanel(roomInvite) {
        const notificationTypeData = {
            type: NotificationTypeEnum.LiveRoomInvite,
            roomId: roomInvite.roomId,
            senderUsername: roomInvite.senderUsername,
            senderUserId: roomInvite.senderUserId,
            senderIcon: roomInvite.senderIcon
        };
        this.addCTANotificationToPanel(notificationTypeData, roomInvite.dateCreated, roomInvite.roomId, this.roomInviteNotifications);
    }
    addFriendRequestNotificationToPanel(friendRequest) {
        const notificationTypeData = {
            type: NotificationTypeEnum.FriendRequestReceived,
            username: friendRequest.username
        };
        this.addCTANotificationToPanel(notificationTypeData, friendRequest.dateCreated, friendRequest.username, this.friendRequestNotifications);
    }
    addCTANotificationToPanel(notificationTypeData, dateCreated, key, notificationsMap) {
        if (this.notificationPanelController == undefined) return;
        const newNotification = this.notificationPanelController.addCTANotification({
            typeData: notificationTypeData,
            dateReceived: dateCreated
        });
        const notificationsFromMap = notificationsMap.get(key);
        if (notificationsFromMap) {
            notificationsFromMap.push(newNotification);
        } else {
            notificationsMap.set(key, [
                newNotification
            ]);
        }
    }
    showRoomInvitePopupNotification(roomInvite) {
        const notificationTranslationObj = {
            key: 'strings:roomPage.userInvitedToRoom',
            options: {
                context: 'checkNotifications',
                username: roomInvite.senderUsername,
                roomId: roomInvite.roomId
            }
        };
        this.showJoinRoomNotification(roomInvite.senderIcon, notificationTranslationObj, roomInvite);
    }
    showFriendRequestPopupNotification(friendRequest) {
        const notificationTranslationObj = {
            key: 'strings:popups.notification.userSentYouFriendRequest',
            options: {
                context: 'noLink',
                username: friendRequest.username
            }
        };
        this.showNotification(LobbyImage.Friends, notificationTranslationObj);
    }
    removeRoomInvitesWithRoomId(roomId) {
        var _this_notificationPanelController;
        const notifications = this.roomInviteNotifications.get(roomId);
        if (!notifications) return;
        for (const notification of notifications)(_this_notificationPanelController = this.notificationPanelController) === null || _this_notificationPanelController === void 0 ? void 0 : _this_notificationPanelController.removeCTANotification(notification);
        this.roomInviteNotifications.delete(roomId);
    }
    removeAllInvites() {
        for (const roomId of this.roomInviteNotifications.keys()){
            this.removeRoomInvitesWithRoomId(roomId);
        }
    }
    displayNotificationIfUserCameOnline(friendData) {
        const enoughTimePassed = this.enoughTimePassedSinceLastNotificationForUser(friendData.userId);
        const didUserComeOnline = this.didUserComeOnline(friendData);
        const canDisplayOnlineNotification = enoughTimePassed && didUserComeOnline;
        if (canDisplayOnlineNotification) this.showNotification(friendData.icon, {
            key: 'strings:roomPage.friendOnline',
            options: {
                username: friendData.username
            }
        });
        this.lastNotificationTimeForUser.set(friendData.userId, Date.now());
        const isNewStatusOffline = friendData.onlineStatus == OnlineStatus.Offline;
        if (isNewStatusOffline) this.onlineFriendUsers.delete(friendData.userId);
        else this.onlineFriendUsers.set(friendData.userId, friendData);
    }
    enoughTimePassedSinceLastNotificationForUser(userId) {
        const lastNotificationTime = this.lastNotificationTimeForUser.get(userId);
        if (lastNotificationTime == undefined) return true;
        const enoughTimePassed = Date.now() - lastNotificationTime > this.ONLINE_NOTIFICATION_THROTTLE_MS;
        return enoughTimePassed;
    }
    didUserComeOnline(newFriendData) {
        const oldFriendData = this.onlineFriendUsers.get(newFriendData.userId);
        const isNewStatusOnline = newFriendData.onlineStatus == OnlineStatus.OnlineAndIdle;
        const isOldStatusOffline = (oldFriendData === null || oldFriendData === void 0 ? void 0 : oldFriendData.onlineStatus) == OnlineStatus.Offline || oldFriendData == undefined;
        return isNewStatusOnline && isOldStatusOffline;
    }
    constructor(notificationPanelController){
        this.roomInviteNotifications = new Map();
        this.friendRequestNotifications = new Map();
        this.onlineFriendUsers = new Map();
        this.lastNotificationTimeForUser = new Map();
        this.ONLINE_NOTIFICATION_THROTTLE_MS = TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MILLISECONDS;
        this.notificationPanelController = notificationPanelController;
    }
}
UIOnlineFriendController.lastOnlineStatus = OnlineStatus.OnlineAndIdle;
